/**
 * Entity-workspace sidebar — the takeover counterpart of AdminSidebar.
 *
 * When an org or provider page takes over the chrome, this renders in the
 * global sidebar's exact slot with its exact shell (width, header band, list
 * styling) so the takeover swaps the sidebar's content, not its look. The
 * header is the exit up one level of the global → org → provider hierarchy:
 * "Admin v2" on an org page, the org name on a provider page.
 */

import { Fragment } from 'react';
import { ArrowBack, OpenInNew } from '@mui/icons-material';
import { Box, Chip, List, ListItemButton, ListItemText, Typography } from '@mui/material';

import type { NavGroup } from './types';

export interface WorkspaceSidebarProps {
  groups: NavGroup[];
  activeKey: string;
  onSelect: (key: string) => void;
  /** Where the header exit leads, e.g. "Admin v2" or the parent org's name. */
  exitLabel: string;
  onExit: () => void;
}

export function WorkspaceSidebar({
  groups,
  activeKey,
  onSelect,
  exitLabel,
  onExit,
}: WorkspaceSidebarProps) {
  return (
    <aside className="w-60 bg-white border-r border-gray-200 flex flex-col h-full shrink-0">
      {/* Header — same band as AdminSidebar's brand button, but it exits up
          one level instead of navigating home. */}
      <button
        type="button"
        className="h-16 w-full flex items-center px-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 text-left"
        onClick={onExit}
        title={exitLabel}
      >
        <ArrowBack className="shrink-0 text-gray-500" />
        <span className="ml-3 text-xl font-semibold text-gray-900 truncate">{exitLabel}</span>
      </button>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        <List component="nav" disablePadding>
          {groups.map(group => (
            <Fragment key={group.label}>
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {group.label}
                </Typography>
              </Box>
              {group.items.map(item => {
                const active = item.key === activeKey;
                return (
                  <ListItemButton
                    key={item.key}
                    onClick={() => onSelect(item.key)}
                    selected={active}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      py: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.50',
                        '&:hover': {
                          backgroundColor: 'primary.100',
                        },
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      slotProps={{
                        primary: {
                          color: active ? 'primary.main' : 'text.primary',
                          sx: {
                            fontSize: '0.875rem',
                            fontWeight: active ? 600 : 400,
                          },
                        },
                      }}
                    />
                    {item.badge && (
                      <Chip
                        label={item.badge.label}
                        size="small"
                        color={
                          item.badge.tone === 'warn'
                            ? 'warning'
                            : item.badge.tone === 'success'
                              ? 'success'
                              : 'default'
                        }
                        variant={item.badge.tone === 'success' ? 'outlined' : 'filled'}
                        sx={{ height: 18, fontSize: 11 }}
                      />
                    )}
                    {item.external && (
                      <OpenInNew sx={{ fontSize: 13, color: 'text.disabled' }} />
                    )}
                  </ListItemButton>
                );
              })}
            </Fragment>
          ))}
        </List>
      </nav>
    </aside>
  );
}
